import React, { useEffect, useState } from 'react'
import ReactSVG from 'react-svg'
import classnames from 'classnames'
import { useQuery, useMutation } from 'react-apollo'
import { useHistory, NavLink } from 'react-router-dom'

import { segmentTrackCancelSubscription, segmentTrackCancelJourney } from '../../../segment/segment-track'
import { checkQueryStatus } from '../../../lib/utils'
import { CANCEL_JOURNEY_BUTTONS } from '../../../segment/constants'
import { CANCEL_SUBSCRIPTION_SCREENS, ACCOUNT_SCREENS } from '../../../constants'
import { LoadingModal as LoadingPanel } from '../../loading/modal'
import CANCEL_OFFER_ELIGIBILITY_QUERY from '../../../../graphql/queries/cancel-offer-eligibility.gql'
import REDEEM_CANCEL_OFFER_MUTATION from '../../../../graphql/mutations/redeem-cancel-offer.gql'
import REMOVE_SUBSCRIPTION_MUTATION from '../../../../graphql/mutations/remove-subscription.gql'
import ACCOUNT_QUERY_WITH_DIR from '../../../../graphql/queries/account-with-directives.gql'
import Button from '../../button'
import VoucherConfirmationModal from './voucher-confirmation-modal'
import backArrowLeft from '../../../../images/back-arrow-left.svg'
import styles from '../cancel-subscription.css'

function OfferContent() {
  const history = useHistory()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [subscriptionAfterRedeem, setSubscriptionAfterRedeem] = useState(null)

  const redeemCta = 'REDEEM OFFER'
  const cancelCta = 'CONFIRM CANCELLATION'

  // Cancel offer eligibility query
  const { data, loading, error } = useQuery(CANCEL_OFFER_ELIGIBILITY_QUERY, {
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    // If the query succeeded and the user is NOT eligible, redirect to the plan and payment page
    if (!loading && data) {
      const isEligible = data.cancelOfferEligibility.isEligible
      if (!isEligible) {
        history.replace(ACCOUNT_SCREENS.SUBSCRIPTION_PAYMENT.path)
      }
    }
  }, [data, loading, error])

  const accountQuery = useQuery(ACCOUNT_QUERY_WITH_DIR, {
    variables: {
      withSubscription: true,
      withClearCachedSubscriptions: true
    },
    fetchPolicy: 'network-only'
  })

  // Redeem cancel offer mutation
  const [redeemCancelOffer,
    {
      data: dataRedeem,
      loading: loadingRedeem
    }
  ] = useMutation(REDEEM_CANCEL_OFFER_MUTATION)

  useEffect(() => {
    if (loadingRedeem) {
      // Track cancel journey event
      segmentTrackCancelJourney({
        step: CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_OFFER.name,
        buttonClicked: CANCEL_JOURNEY_BUTTONS.REDEEM_OFFER
      })
    }

    if (!loadingRedeem && dataRedeem?.redeemCancelOffer?.successful) {
      setSubscriptionAfterRedeem(dataRedeem.redeemCancelOffer.subscriptionAfterRedeem)

      setShowConfirmation(true)
    }
  }, [dataRedeem, loadingRedeem])

  // Remove subscription mutation
  const [
    removeSubscription,
    { loading: loadingRemove, data: dataRemove }
  ] = useMutation(REMOVE_SUBSCRIPTION_MUTATION)

  // If remove subscription mutation was success, redirect to the confirmed cancellation page
  useEffect(() => {
    if (loadingRemove) {
      // Track cancel journey event
      segmentTrackCancelJourney({
        step: CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_OFFER.name,
        buttonClicked: CANCEL_JOURNEY_BUTTONS.CONFIRM_CANCELLATION
      })
    }

    if (!loadingRemove && dataRemove && dataRemove.removeSubscription) {
      history.push(CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_CONFIRMATION.path)
    }
  }, [loadingRemove, dataRemove])

  const remove = () => {
    const subscription = accountQuery.data?.account?.subscription
    const { currentSubscription, upcomingSubscription } = subscription || {}
    const sku = upcomingSubscription?.sku || currentSubscription?.sku

    if (!sku) history.replace(ACCOUNT_SCREENS.SUBSCRIPTION_PAYMENT.path)

    removeSubscription({ variables: { sku } })

    // Track cancel subscription event
    segmentTrackCancelSubscription({ sku })
  }

  if (checkQueryStatus({ loading, error }) || checkQueryStatus(accountQuery)) {
    return <LoadingPanel />
  }

  const {
    originalPrice,
    offerPrice,
    offerCopy,
    billingPeriod
  } = data.cancelOfferEligibility?.cancelOfferDetails ?? {}

  const [priceBeforeDot, priceAfterDot] = parseFloat(offerPrice).toFixed(2).split('.')

  return (
    <div className={styles.popupWrapper}>
      {!showConfirmation && (
        <div className={styles.popup}>
          <NavLink
            to={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_LANDING.path}
            className={styles.modalLink}
          >
            <ReactSVG
              beforeInjection={(svg) => {
                svg.setAttribute('class', styles.linkArrowWhite)
              }}
              src={backArrowLeft}
            />
            <span className={classnames(styles.linkText, styles.whiteColor)}>
              Go Back
            </span>
          </NavLink>
          <div className={styles.section}>
            <span className={classnames(styles.badge, styles.whiteBadge)}>
              YOUR ONE TIME OFFER
            </span>
            <div className={styles.header}>
              {offerCopy}
            </div>
            <div className={styles.copy}>
              Keep watching what you love with Premium titles and smart suggestions.&nbsp;
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://help.neontv.co.nz/support/solutions/articles/64000309997-neon-cancellation-promotion-faqs-and-terms"
                style={{ color: 'white', textDecoration: 'underline', textUnderlineOffset: '3px' }}
              >
                T&Cs apply
              </a>
              .
              <div className={styles.originalPrice}>
                $
                {originalPrice}
                /
                {billingPeriod}
              </div>
              <div className={styles.priceAmount}>
                <span className={styles.currency}>$</span>
                <span className={styles.priceAmountBeforeDot}>{priceBeforeDot}</span>
                <div className={styles.priceAmountRightFrame}>
                  <span className={styles.priceAmountAfterDot}>{priceAfterDot}</span>
                  <span className={styles.priceAmountUnit}>
                    /
                    {billingPeriod}
                  </span>
                </div>
              </div>
              <Button
                className={styles.button}
                text={redeemCta}
                onClick={redeemCancelOffer}
                disabled={loadingRedeem}
              />
              <Button
                className={styles.button}
                text={cancelCta}
                onClick={remove}
                disabled={loadingRemove}
                darkBGSecondary
              />
            </div>
          </div>
        </div>
      )}
      {showConfirmation && subscriptionAfterRedeem && (
        <VoucherConfirmationModal
          subscriptionAfterRedeem={subscriptionAfterRedeem}
        />
      )}
    </div>
  )
}

export default OfferContent
